<template>
    <div>
        <div class="flex flex-col min-h-0 h-full p-6 pt-5 | 2xl:p-8 2xl:pt-8">
            <page-header :title="$t('global.mainNav.automations')">
                <nio-button
                    color="blue"
                    icon="regular/add"
                    @click="triggerCreate"
                >
                    {{ $t('global.general.add') }}
                </nio-button>
            </page-header>

            <content-box :loading="$wait.is('fetching.automations')">
                <template #header>
                    <content-box-tabs :active-tab.sync="filters.tab" :tabs="tabs" />
                </template>
                <template #body>
                    <nio-table
                        :columns="columns"
                        :rows="filteredAutomations"
                        :selections.sync="selections"
                        ref="body"
                    >
                        <template #no-results>
                            <figure class="mb-6 text-3xl select-none">
                                ¯\_(ツ)_/¯
                            </figure>
                            <p class="mb-2 text-xl">
                                {{ $t('automations.list.noAutomations') }}
                            </p>
                            <p class="mb-4">
                                {{ $t('automations.list.wouldYouLikeToAddOne') }}
                            </p>
                            <nio-button color="blue" icon="regular/add" @click="triggerCreate">
                                {{ $t('global.general.add') }}
                            </nio-button>
                        </template>

                        <template v-slot:[`column-${column.name}`]="{ row: automation }" v-for="column in columns">
                            <automation-list-item
                                :automation="automation"
                                :column="column.name"
                                :truncate="column.truncate"
                                :key="column.name"
                                @delete="triggerDelete(automation)"
                                @update="triggerUpdate(automation)"
                            />
                        </template>
                    </nio-table>
                </template>
                <template #footer>
                    <div class="flex-1 flex items-center justify-start p-4">
                        <nio-dropdown :disabled="!selectedAutomations.length" placement="top-start">
                            <template #trigger>
                                <nio-button color="blue">
                                    {{ $t('automations.list.bulkAction') }}
                                    <nio-icon class="text-xs ml-2" name="regular/arrow-up-1" />
                                </nio-button>
                            </template>

                            <nio-dropdown-item
                                class="hover:text-red-500"
                                icon="regular/bin-2-alternate"
                                @click="onBulkActionClick('delete')"
                            >
                                {{ $t('automations.list.delete') }}
                            </nio-dropdown-item>
                            <nio-dropdown-item
                                divided-top
                                icon="regular/controls-pause"
                                @click="onBulkActionClick('pause')"
                            >
                                {{ $t('automations.list.pause') }}
                            </nio-dropdown-item>
                            <nio-dropdown-item
                                icon="regular/controls-play"
                                @click="onBulkActionClick('start')"
                            >
                                {{ $t('automations.list.run') }}
                            </nio-dropdown-item>
                        </nio-dropdown>
                    </div>
                </template>
            </content-box>
        </div>

        <automation-modal />

        <confirm-modal
            :opened.sync="openDelete"
            :title="$t('automations.list.deleteModal.header')"
            @confirm="deleteSelected"
            @deny="toDelete = null"
        >
            <p slot="message">
                {{ $t('automations.list.deleteModal.message') }}
            </p>
        </confirm-modal>

        <filters-in-url :filters="filters" />
    </div>
</template>

<script>
    import { mapState, mapGetters, mapMutations } from 'vuex';
    import AutomationListItem from '@/views/Automations/AutomationListItem.vue';
    import AutomationModal from '@/components/Modals/AutomationModal.vue';
    import ConfirmModal from '@/components/Modals/ConfirmModal.vue';
    import ContentBox from '@/components/ContentBox.vue';
    import ContentBoxTabs from '@/components/ContentBoxTabs.vue';
    import FiltersInUrl from '@/components/Utils/FiltersInUrl.js';
    import PageHeader from '@/components/PageHeader.vue';

    export default {
        components: {
            AutomationListItem,
            AutomationModal,
            ConfirmModal,
            ContentBox,
            ContentBoxTabs,
            FiltersInUrl,
            PageHeader,
        },

        data() {
            return {
                openDelete: false,
                selections: [],
                toDelete: null,
                filters: {
                    tab: 'all',
                },
            };
        },

        computed: {
            ...mapState('automation', [
                'automations',
                'selectedAutomations',
            ]),

            ...mapGetters('automation', [
                'runningAutomations',
                'pausedAutomations',
            ]),

            filteredAutomations() {
                let filteredAutomations = [];

                switch (this.filters.tab) {
                    case 'running':
                        filteredAutomations = this.runningAutomations;
                        break;

                    case 'paused':
                        filteredAutomations = this.pausedAutomations;
                        break;

                    default:
                        filteredAutomations = this.automations;
                        break;
                }

                return filteredAutomations;
            },

            tabs() {
                return [
                    {
                        value: 'all',
                        text: `${this.$t('automations.list.allTab')} (${this.automations.length})`,
                        icon: 'regular/navigation-menu',
                    },
                    {
                        value: 'running',
                        text: `${this.$t('automations.list.runningTab')} (${this.runningAutomations.length})`,
                        icon: 'regular/controls-play',
                    },
                    {
                        value: 'paused',
                        text: `${this.$t('automations.list.pausedTab')} (${this.pausedAutomations.length})`,
                        icon: 'regular/controls-pause',
                    },
                ];
            },

            columns() {
                return [
                    {
                        name: 'name',
                        className: 'flex-3',
                    },
                    {
                        name: 'status',
                        className: 'flex-1',
                        truncate: false,
                    },
                    {
                        name: 'type',
                        className: 'flex-1',
                        truncate: false,
                    },
                    {
                        name: 'owner',
                        className: 'flex-3',
                        truncate: false,
                    },
                    {
                        name: 'weekdays',
                        className: 'flex-3',
                        align: 'center',
                        truncate: false,
                    },
                    {
                        name: 'actions',
                        className: 'flex-1',
                        align: 'right',
                        truncate: false,
                    },
                ];
            },
        },

        watch: {
            selections(newValue) {
                const automations = this.filteredAutomations.filter(a => newValue.includes(a.id));

                this.setSelectedAutomations(automations);
            },

            'filters.tab'() {
                this.reset();
            },
        },

        methods: {
            ...mapMutations('automation', [
                'setSelectedAutomation',
                'setSelectedAutomations',
                'updateAutomation',
                'deleteAutomation',
            ]),

            onBulkActionClick(command) {
                switch (command) {
                    case 'delete':
                        this.openDelete = true;
                        break;
                    case 'pause':
                        this.setSelectedAutomationsState('pause');
                        break;
                    case 'start':
                        this.setSelectedAutomationsState('start');
                        break;
                    default:
                        break;
                }
            },

            reset() {
                return new Promise(resolve => {
                    this.selections = [];
                    this.toDelete = null;

                    this.$nextTick(() => {
                        resolve();
                    });
                });
            },

            triggerCreate() {
                this.setSelectedAutomation(null);

                this.$modal.open('automation');
            },

            triggerUpdate(automation) {
                this.setSelectedAutomation(automation);

                this.$modal.open('automation');
            },

            triggerDelete(automation) {
                this.toDelete = automation;
                this.openDelete = true;
            },

            // @TODO Transfer in Vuex
            deleteSelected() {
                let automations = this.selectedAutomations;

                if (this.toDelete) {
                    automations = [this.toDelete];
                }

                for (const automation of automations) {
                    this.deleteAutomation(automation);

                    this.$axios.delete(`automations/${automation.id}`)
                        .catch(() => {
                            this.filteredAutomations.push(automation); // @TODO Revamp this to push to Vuex instead
                        });
                }

                this.reset();
            },

            setSelectedAutomationsState(action) {
                for (const automation of this.selectedAutomations) {
                    switch (action) {
                        case 'start':
                            this.setStatus(automation, 'running');
                            break;

                        case 'pause':
                            this.setStatus(automation, 'paused');
                            break;

                        default:
                            break;
                    }
                }
            },

            // @TODO Transfer in Vuex
            setStatus(automation, state) {
                this.updateAutomation({
                    id: automation.id,
                    state,
                });

                this.$axios.put(`automations/${automation.id}`, {
                    state,
                });
            },
        },

        beforeRouteLeave(to, from, next) {
            this.reset().then(() => {
                next();
            });
        },
    };
</script>
